import React from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline' | 'dark';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-gradient-to-r from-brand-700 to-brand-600 text-white hover:from-brand-600 hover:to-brand-500 shadow-button active:scale-[0.98]',
  secondary:
    'bg-white text-brand-950 border border-gray-200 hover:bg-gray-50 shadow-button active:scale-[0.98]',
  danger:
    'bg-red-600 text-white hover:bg-red-700 shadow-button active:scale-[0.98]',
  ghost:
    'bg-transparent text-brand-100 hover:bg-white/10 active:scale-[0.98]',
  outline:
    'bg-transparent border border-white/30 text-white hover:bg-white/10 active:scale-[0.98]',
  dark:
    'bg-black/40 backdrop-blur-md border border-brand-800/50 text-white hover:bg-black/50 active:scale-[0.98]',
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-2 text-sm rounded-lg',
  md: 'px-4 py-3 text-sm rounded-xl',
  lg: 'px-6 py-3.5 text-base rounded-xl',
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  children,
  className = '',
  disabled,
  ...props
}) => {
  const base =
    'inline-flex items-center justify-center gap-2 font-semibold transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-950 disabled:opacity-50 disabled:cursor-not-allowed';
  const classes = [base, variantClasses[variant], sizeClasses[size], fullWidth ? 'w-full' : ''].join(' ');

  return (
    <button className={`${classes} ${className}`} disabled={disabled || isLoading} {...props}>
      {isLoading && (
        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      )}
      {!isLoading && leftIcon}
      {children}
      {!isLoading && rightIcon}
    </button>
  );
};

export default Button;
